import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Boxes, Plus, Trash2, Pencil, ArrowRightLeft, History, Download, AlertTriangle } from "lucide-react";
import { MultiSelect } from "../components/MultiSelect";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

const statusColors = {
  available: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
  allotted: "bg-accent/15 text-accent",
  maintenance: "bg-primary/10 text-primary",
  retired: "bg-destructive/10 text-destructive",
};
const CATEGORIES = ["Tool", "Dress", "Machinery", "Other"];
const ACTIONS = [
  { v: "allot", l: "Allot to employee" },
  { v: "return", l: "Return / Delink" },
  { v: "transfer", l: "Reallot to another employee" },
  { v: "replace", l: "Replace (damaged)" },
  { v: "maintenance", l: "Maintenance" },
  { v: "retire", l: "Retire" },
];

export default function Assets() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [catFilter, setCatFilter] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Tool", quantity: "1", description: "", purchase_date: today() });
  const [move, setMove] = useState(null);
  const [moveForm, setMoveForm] = useState({ action: "allot", employee_id: "", person: "", note: "", date: today() });
  const [hist, setHist] = useState(null);
  const [histRows, setHistRows] = useState([]);

  const load = async () => { setAssets((await api.get("/assets")).data); setSelected(new Set()); };
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([])); }, []);

  const shown = assets.filter((a) => catFilter.length === 0 || catFilter.includes(a.category));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = shown.length > 0 && shown.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(shown.map((a) => a.id)));

  const exportSelected = () => {
    const picked = shown.filter((a) => selected.has(a.id));
    if (picked.length === 0) return toast.error("Select at least one asset");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["asset_code", "name", "category", "quantity", "status", "allotted_to", "emp_code", "allotted_date", "purchase_date"];
    const lines = [header.join(",")].concat(picked.map((a) => [a.asset_code, a.name, a.category, a.quantity, a.status, a.allotted_to, a.allotted_emp_code, a.allotted_date, a.purchase_date].map(esc).join(",")));
    const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = `assets_export_${today()}.csv`;
    el.click();
    URL.revokeObjectURL(el.href);
    toast.success(`Exported ${picked.length} asset(s)`);
  };

  const delSelected = async () => {
    const picked = shown.filter((a) => selected.has(a.id));
    if (picked.length === 0) return toast.error("Select at least one asset");
    if (!window.confirm(`Delete ${picked.length} asset(s)? Their movement history will be removed.`)) return;
    let ok = 0, fail = 0;
    for (const a of picked) { try { await api.delete(`/assets/${a.id}`); ok++; } catch { fail++; } }
    toast[fail ? "info" : "success"](`${ok} deleted${fail ? `, ${fail} failed` : ""}`);
    load();
  };

  const resetForm = () => { setForm({ name: "", category: "Tool", quantity: "1", description: "", purchase_date: today() }); setEdit(null); };

  const saveAsset = async () => {
    try {
      if (!form.name) return toast.error("Asset name required");
      const payload = {
        name: form.name, category: form.category,
        quantity: parseFloat(form.quantity) || 1, description: form.description, purchase_date: form.purchase_date,
      };
      if (edit) await api.put(`/assets/${edit}`, payload);
      else await api.post("/assets", payload);
      toast.success(edit ? "Asset updated" : "Asset added with a unique code");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (a) => {
    setEdit(a.id);
    setForm({ name: a.name, category: a.category, quantity: String(a.quantity || 1), description: a.description || "", purchase_date: a.purchase_date || today() });
    setOpen(true);
  };

  const delAsset = async (a) => {
    if (!window.confirm(`Delete asset ${a.asset_code} — ${a.name}? Its movement history will be removed.`)) return;
    try { await api.delete(`/assets/${a.id}`); toast.success("Asset deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openMove = (a) => { setMove(a); setMoveForm({ action: a.status === "allotted" ? "return" : "allot", employee_id: "", person: "", note: "", date: today() }); };

  const saveMove = async () => {
    try {
      const needsEmp = ["allot", "transfer"].includes(moveForm.action);
      if (needsEmp && !moveForm.employee_id) return toast.error("Select an employee");
      if (moveForm.action === "replace" && !moveForm.note) return toast.error("Enter the replacement details");
      await api.post("/asset-movements", { asset_id: move.id, ...moveForm });
      toast.success("Movement logged with a unique number");
      setMove(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openHist = async (a) => {
    setHist(a);
    setHistRows((await api.get("/asset-movements", { params: { asset_id: a.id } })).data);
  };

  const [dmg, setDmg] = useState(null);
  const [dmgReturns, setDmgReturns] = useState([]);
  const openDmg = async () => {
    const [rep, ret] = await Promise.all([api.get("/assets/replacements"), api.get("/assets/returns")]);
    setDmgReturns(ret.data || []);
    setDmg(rep.data);
  };

  return (
    <Layout>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Asset Register" subtitle="Tools, dresses & machinery — each gets a unique code to punch on the item, with full allotment history.">
          {canWrite && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <button data-testid="add-asset-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                  <Plus size={16} /> Asset
                </button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader><DialogTitle className="font-display text-2xl">{edit ? "Edit Asset" : "New Asset"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><label className={label}>Asset name</label>
                    <input data-testid="asset-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Drill machine, Uniform set…" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Category</label>
                      <select data-testid="asset-category" className={field} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select></div>
                    <div><label className={label}>Quantity</label>
                      <input data-testid="asset-qty" type="number" className={field} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Purchase date</label>
                      <input data-testid="asset-date" type="date" className={`${field} num`} value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
                    <div><label className={label}>Description / notes</label>
                      <input data-testid="asset-desc" className={field} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="optional" /></div>
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="asset-save" onClick={saveAsset} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">{edit ? "Save changes" : "Add asset"}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </PageHeader>

        <div className="flex flex-wrap items-end gap-3 mb-5">
          <MultiSelect label="Category" testid="asset-cat-filter" options={CATEGORIES}
            selected={catFilter} onChange={setCatFilter} placeholder="All categories" />
          {catFilter.length > 0 && (
            <button data-testid="asset-clear-filter" onClick={() => setCatFilter([])}
              className="h-10 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
          )}
          <div className="flex-1" />
          <button data-testid="damaged-register-btn" onClick={openDmg}
            className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors">
            <AlertTriangle size={15} /> Damaged / Replacement register
          </button>
        </div>

        <Dialog open={!!dmg} onOpenChange={(o) => !o && setDmg(null)}>
          <DialogContent className="bg-card max-h-[85vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Damaged / Replacement register</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2 mb-1">Every item that was replaced because it was damaged, with the date and details — kept for audit.</p>
            <div className="rounded-lg border border-border divide-y divide-border">
              <div className="grid grid-cols-[auto_1fr_1fr] gap-3 px-3 py-2 bg-muted text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Date</span><span>Asset</span><span>Details</span>
              </div>
              {(dmg || []).map((m) => (
                <div key={m.id} data-testid={`dmg-row-${m.id}`} className="grid grid-cols-[auto_1fr_1fr] gap-3 px-3 py-2 items-start text-sm">
                  <span className="num text-muted-foreground whitespace-nowrap">{m.date}</span>
                  <span className="min-w-0"><span className="num text-xs font-bold text-primary mr-1">{m.asset_code}</span>{m.asset_name}{m.person ? <span className="text-xs text-muted-foreground block">held by {m.person}</span> : null}</span>
                  <span className="min-w-0 break-words">{m.note || "—"}</span>
                </div>
              ))}
              {dmg && dmg.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No replacements logged yet.</div>}
            </div>
            <div className="text-sm font-semibold mt-4 mb-1">Returns &amp; Retires</div>
            <p className="text-xs text-muted-foreground mb-1">Assets returned/delinked or retired, with dates — full audit trail.</p>
            <div className="rounded-lg border border-border divide-y divide-border">
              <div className="grid grid-cols-[auto_auto_1fr_1fr] gap-3 px-3 py-2 bg-muted text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Date</span><span>Action</span><span>Asset</span><span>Details</span>
              </div>
              {dmgReturns.map((m) => (
                <div key={m.id} data-testid={`return-row-${m.id}`} className="grid grid-cols-[auto_auto_1fr_1fr] gap-3 px-3 py-2 items-start text-sm">
                  <span className="num text-muted-foreground whitespace-nowrap">{m.date}</span>
                  <span className="capitalize text-xs font-medium">{m.action}</span>
                  <span className="min-w-0"><span className="num text-xs font-bold text-primary mr-1">{m.asset_code}</span>{m.asset_name}{m.person ? <span className="text-xs text-muted-foreground block">was with {m.person}</span> : null}</span>
                  <span className="min-w-0 break-words">{m.note || "—"}</span>
                </div>
              ))}
              {dmgReturns.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No returns or retires logged yet.</div>}
            </div>
          </DialogContent>
        </Dialog>

        {selected.size > 0 && (
          <div data-testid="asset-bulk-bar" className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex-1" />
            <button data-testid="asset-bulk-export" onClick={exportSelected}
              className="h-9 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors">
              <Download size={14} /> Export CSV
            </button>
            {canWrite && (
              <button data-testid="asset-bulk-del" onClick={delSelected}
                className="h-9 px-3 rounded-md border border-destructive/30 bg-card text-sm text-destructive flex items-center gap-1.5 hover:bg-destructive/10 transition-colors">
                <Trash2 size={14} /> Delete selected
              </button>
            )}
            <button data-testid="asset-bulk-clear" onClick={() => setSelected(new Set())}
              className="h-9 px-3 rounded-md border border-input bg-card text-sm hover:bg-muted transition-colors">Clear</button>
          </div>
        )}

        {shown.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Boxes className="mx-auto mb-3 opacity-40" size={32} /> No assets yet. Add tools, dresses or machinery to track them.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
            <div className="min-w-[900px]">
              <div className="grid grid-cols-[auto_auto_1.4fr_auto_1.2fr_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <input data-testid="asset-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                <span>Code</span><span>Asset</span><span className="text-center">Status</span><span>Allotted to</span><span className="text-right">Actions</span>
              </div>
              <div className="divide-y divide-border">
                {shown.map((a) => (
                  <div key={a.id} data-testid={`asset-row-${a.id}`} className={`grid grid-cols-[auto_auto_1.4fr_auto_1.2fr_auto] gap-3 px-5 py-3 items-center ${selected.has(a.id) ? "bg-accent/5" : ""}`}>
                    <input data-testid={`asset-select-${a.id}`} type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSel(a.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                    <span className="num text-sm font-bold text-primary tracking-wide bg-primary/5 px-2 py-1 rounded border border-primary/10" data-testid={`asset-code-${a.id}`}>{a.asset_code}</span>
                    <div className="min-w-0"><div className="font-medium truncate">{a.name}</div><div className="text-xs text-muted-foreground truncate">{a.category}{a.quantity > 1 ? ` · qty ${a.quantity}` : ""}</div></div>
                    <div className="flex justify-center"><span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${statusColors[a.status] || statusColors.available}`}>{a.status}</span></div>
                    <div className="min-w-0 text-sm">
                      {a.allotted_to ? (
                        <>
                          <div className="truncate font-medium">{a.allotted_to}{a.allotted_emp_code ? <span className="num text-[10px] bg-muted px-1 py-0.5 rounded ml-1 text-muted-foreground">{a.allotted_emp_code}</span> : null}</div>
                          {a.allotted_date ? <div className="text-xs text-muted-foreground num">since {a.allotted_date}</div> : null}
                        </>
                      ) : <span className="text-muted-foreground">—</span>}
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {canWrite && (
                        <button data-testid={`asset-move-${a.id}`} onClick={() => openMove(a)} className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted transition-colors"><ArrowRightLeft size={13} /> Allot/Move</button>
                      )}
                      <button data-testid={`asset-hist-${a.id}`} onClick={() => openHist(a)} className="text-muted-foreground hover:text-primary transition-colors"><History size={15} /></button>
                      {canWrite && <button data-testid={`asset-edit-${a.id}`} onClick={() => openEdit(a)} className="text-muted-foreground hover:text-primary transition-colors"><Pencil size={14} /></button>}
                      {canWrite && <button data-testid={`asset-del-${a.id}`} onClick={() => delAsset(a)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Movement dialog */}
        <Dialog open={!!move} onOpenChange={(o) => !o && setMove(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Asset Movement</DialogTitle></DialogHeader>
            {move && (
              <div className="space-y-3">
                <div className="rounded-md bg-muted p-3 text-sm flex justify-between">
                  <span className="num font-bold text-primary">{move.asset_code}</span><span className="truncate">{move.name}</span>
                </div>
                <div>
                  <label className={label}>Action</label>
                  <select data-testid="move-action" className={field} value={moveForm.action} onChange={(e) => setMoveForm({ ...moveForm, action: e.target.value })}>
                    {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                  </select>
                </div>
                {["allot", "transfer"].includes(moveForm.action) && (
                  <div><label className={label}>{moveForm.action === "transfer" ? "Reallot to employee" : "Allot to employee"}</label>
                    <select data-testid="move-employee" className={field} value={moveForm.employee_id}
                      onChange={(e) => { const emp = employees.find((x) => x.id === e.target.value); setMoveForm({ ...moveForm, employee_id: e.target.value, person: emp ? emp.name : "" }); }}>
                      <option value="">Select employee…</option>
                      {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}{emp.emp_code ? ` (${emp.emp_code})` : ""}{emp.designation ? ` — ${emp.designation}` : ""}</option>)}
                    </select>
                    {employees.length === 0 && <p className="text-xs text-muted-foreground mt-1">No employees yet — add them in the Salary page first.</p>}
                  </div>
                )}
                {moveForm.action === "replace" && (
                  <p className="text-xs text-muted-foreground -mt-1">Logs a replacement for the damaged item and keeps it allotted to {move?.allotted_to || "the current holder"}. Enter the replacement details in the note.</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>{["allot", "transfer"].includes(moveForm.action) ? "Allotted date" : "Date"}</label>
                    <input data-testid="move-date" type="date" className={`${field} num`} value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} /></div>
                  <div><label className={label}>{moveForm.action === "replace" ? "Replacement details" : "Note"}</label>
                    <input data-testid="move-note" className={field} value={moveForm.note} onChange={(e) => setMoveForm({ ...moveForm, note: e.target.value })} placeholder={moveForm.action === "replace" ? "e.g. new shoes issued, old pair torn" : "optional"} /></div>
                </div>
                <p className="text-xs text-muted-foreground">Every movement gets a unique reference number for tracking.</p>
              </div>
            )}
            <DialogFooter>
              <button data-testid="move-save" onClick={saveMove} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Log movement</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History dialog */}
        <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
          <DialogContent className="bg-card max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">History · {hist?.asset_code}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {histRows.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No movements logged yet.</div>
              ) : histRows.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><span className="num text-xs text-primary font-bold">{m.ref_no}</span><span className="capitalize font-medium">{m.action}</span>{m.person && <span className="text-muted-foreground truncate">→ {m.person}{m.emp_code ? ` (${m.emp_code})` : ""}</span>}</div>
                    <div className="text-xs text-muted-foreground num">{m.date}{m.note ? ` · ${m.note}` : ""}</div>
                  </div>
                  {["allot", "transfer"].includes(m.action) && (
                    <button data-testid={`asset-slip-${m.id}`} onClick={async () => {
                      try {
                        const res = await api.get(`/asset-movements/${m.id}/slip`, { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a"); a.href = url; a.download = `allotment_slip_${m.ref_no}.pdf`; a.click();
                        window.URL.revokeObjectURL(url); toast.success("Slip downloaded");
                      } catch { toast.error("Download failed"); }
                    }} className="shrink-0 ml-2 h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted transition-colors"><Download size={12} /> Slip</button>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
